import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  ESIGN_DOCUMENT_DEFAULT_HEIGHT,
  ESignDocument,
  Link,
  Modal,
  ModalTrigger,
  PrimaryButton,
  Text,
} from "@certn/cascadia-react";
import { useContract } from "@certn/cascadia-react/miscellaneous/contract/hooks";
import { css } from "@emotion/css";
import { useMemo } from "react";
import { Trans } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { ConsentListItem, isSuperDocConsent } from "./consentListItem";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { createESignDownloadHandler } from "../../esign/createESignDownloadHandler";
import { CLICKWRAP_ACCESS_ID } from "../constants";

interface Props {
  consent: ConsentListItem;
}

export function ConsentModal(props: Props) {
  const { enableSuperdocApplicantAgreementTemplates } = useFlags();
  if (
    isSuperDocConsent(props.consent, enableSuperdocApplicantAgreementTemplates)
  ) {
    return <SuperDocConsentModal consent={props.consent} />;
  }
  return <PactSafeConsentModal consent={props.consent} />;
}

function PactSafeConsentModal(props: Props) {
  const { caseId } = useApplicationFormContext();

  const { renderData, contractIDs } = useMemo(
    () => ({
      renderData: props.consent.render_data,
      contractIDs: [props.consent.external_id],
    }),
    [props.consent.external_id, props.consent.render_data]
  );

  const {
    data: contractsData,
    getContractHTML,
    isError,
  } = useContract({
    accessID: CLICKWRAP_ACCESS_ID,
    contractIDs,
    signerID: caseId,
    renderData,
  });

  const contractHTML =
    Boolean(contractsData && !isError) &&
    getContractHTML(props.consent.external_id);

  return (
    <ModalTrigger
      trigger={
        <Link
          textStyle="form/body-secondary-link"
          testID={`${props.consent.external_id}-modal-link`}
          linkStyle="monochrome"
        >
          {props.consent.template_name}
        </Link>
      }
    >
      <Modal>
        <Dialog>
          {({ close }) => (
            <>
              <DialogHeader>{props.consent.template_name}</DialogHeader>

              <DialogContent>
                <div
                  className={styles.content}
                  dangerouslySetInnerHTML={
                    isError
                      ? undefined
                      : {
                          __html: contractHTML,
                        }
                  }
                  aria-label="contract-content"
                  role="textbox"
                  tabIndex={0}
                >
                  {isError && (
                    <Text color="text-weak" testID="contract-fetch-error">
                      <Trans>Something went wrong</Trans>
                    </Text>
                  )}
                </div>
              </DialogContent>

              <DialogFooter
                leftContent={
                  <Link
                    testID="contract-download-link"
                    href={
                      contractsData?.contract_data[props.consent.external_id]
                        ?.download_url
                    }
                    target="_blank"
                    textColor="text-weak"
                    textStyle="global/body-secondary"
                  >
                    <Trans>Download</Trans>
                  </Link>
                }
              >
                <PrimaryButton onPress={close}>
                  <Trans>Close</Trans>
                </PrimaryButton>
              </DialogFooter>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalTrigger>
  );
}

function SuperDocConsentModal(props: Props) {
  const { caseId } = useApplicationFormContext();
  const onDownload = useMemo(
    () =>
      props.consent.id
        ? createESignDownloadHandler(caseId, props.consent.id)
        : undefined,
    [caseId, props.consent.id]
  );

  return (
    <ModalTrigger
      trigger={
        <Link
          textStyle="form/body-secondary-link"
          testID={`${props.consent.id}-modal-link`}
          linkStyle="monochrome"
        >
          {props.consent.template_name}
        </Link>
      }
    >
      <Modal>
        <Dialog width={640}>
          {({ close }) => (
            <>
              <DialogHeader>{props.consent.template_name}</DialogHeader>

              <DialogContent>
                <div className={styles.esignContainer}>
                  <ESignDocument
                    data={props.consent.esign}
                    onDownload={onDownload}
                    downloadFilename={props.consent.template_name}
                  />
                </div>
              </DialogContent>

              <DialogFooter>
                <PrimaryButton onPress={close}>
                  <Trans>Close</Trans>
                </PrimaryButton>
              </DialogFooter>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalTrigger>
  );
}

const styles = {
  content: css({
    flex: 1,
    overflow: "auto",
    // text color for Clickwrap document
    "& *": {
      color: `var(--text-weak) !important`,
    },
    // override Clickwrap styles
    "& .ps-contract-scroll": {
      maxHeight: "unset !important",
      padding: "0 !important",
      border: "none !important",
    },
  }),
  esignContainer: css({
    height: ESIGN_DOCUMENT_DEFAULT_HEIGHT,
    width: "100%",
    "& > *": {
      height: "100%",
      width: "100%",
    },
  }),
};
